import { BookOpen, Check, ChevronDown, LogOut, Settings as SettingsIcon, User as UserIcon } from "lucide-react";

import { useAuth } from "@rook/shared/auth/AuthProvider";
import { EnvSwitchButton } from "@rook/shared/components/EnvSwitchButton";
import { ENVIRONMENT_OPTIONS, useApp, type Environment } from "@rook/shared/lib/appContext";
import { cn } from "@rook/shared/lib/cn";
import { portalOriginForEnv } from "@rook/shared/lib/portalOrigins";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  UserMenu,
} from "@rook/ui";

/**
 * Per-env brand copy + accent color. The `spinnerColor` is the
 * EnvSwitchOverlay's spinner ring (CPF-UIR-089 env-color emphasis).
 * The Tailwind `tone` class is used for the in-Topbar label.
 */
const envCopy: Record<
  Environment,
  { label: string; tone: string; spinnerColor: string }
> = {
  sandbox: { label: "Sandbox", tone: "text-emerald-300", spinnerColor: "#34d399" },
  development: { label: "Development", tone: "text-amber-300", spinnerColor: "#fbbf24" },
  production: { label: "Production", tone: "text-rose-300", spinnerColor: "#fb7185" },
};

/**
 * Env switcher — opens a dropdown that lists the three envs; the
 * current env is shown as a non-interactive checkmark row, and each
 * OTHER env is rendered as an `<EnvSwitchButton>` that navigates the
 * browser cross-origin to that env's portal (CPF-NFR-028) using the
 * live origin from `portalOriginForEnv` (cutover-aware).
 *
 * Why this is NOT the legacy `useApp().setEnv(...)` pattern: each
 * per-env Amplify bundle is env-locked (CPF-NFR-034) — flipping
 * `env` in React state on the SAME host would (a) leave the URL on
 * the prod host while proxying `/api/proxy/clients/sandbox/...`,
 * (b) send the prod-issued Cognito Bearer to the sandbox upstream,
 * (c) collect 5xx across every chart. See `foundations/AI-PITFALLS.md`
 * P-033 for the full story.
 *
 * Preserves the current `targetPath` so a click from `/home/logs?x=1`
 * lands on `/home/logs?x=1` in the destination env — the customer
 * keeps their context across the switch. The path is read from
 * `window.location` at click time (inside EnvSwitchButton), so we
 * just pass it through here.
 */
const EnvSwitcher = () => {
  const { env } = useApp();
  const current = envCopy[env];

  // Preserve the customer's current path + query + hash across the
  // env switch so context is maintained. Falls back to "/" in SSR /
  // jsdom where `window` is unavailable. Computed once per render —
  // the EnvSwitchButton click handler reads it from props.
  const currentPath =
    typeof window === "undefined"
      ? "/"
      : `${window.location.pathname}${window.location.search}${window.location.hash}` ||
        "/";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className="gap-2"
          data-tour="topbar-env"
        >
          <span className="text-[10px] font-semibold uppercase tracking-widest text-rook-olive">
            Env
          </span>
          <span className={cn("font-medium", current.tone)}>{current.label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-rook-olive" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Environment</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ENVIRONMENT_OPTIONS.map((option) => {
          const isActive = option === env;
          const copy = envCopy[option];

          // The current env is a passive row (no navigation). Showing
          // it disabled-but-marked keeps the visual symmetry of the
          // dropdown (Stripe / Vercel use the same affordance).
          if (isActive) {
            return (
              <DropdownMenuItem
                key={option}
                disabled
                className="flex items-center gap-2 opacity-100"
                aria-current="true"
              >
                <Check className={cn("h-3.5 w-3.5", copy.tone)} />
                <span className={copy.tone}>{copy.label}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-rook-olive">
                  current
                </span>
              </DropdownMenuItem>
            );
          }

          // Other envs: cross-origin navigation via EnvSwitchButton.
          // `asChild` on DropdownMenuItem would dodge the menu's own
          // onSelect-then-close handling and double-trigger the
          // navigation; instead, we render the button as the row's
          // content. Radix dropdowns let any focusable child receive
          // keyboard focus + Enter so the menu still works with the
          // keyboard.
          return (
            <DropdownMenuItem
              key={option}
              asChild
              className="p-0 focus:bg-transparent"
            >
              <EnvSwitchButton
                targetOrigin={portalOriginForEnv(option)}
                targetEnvLabel={copy.label}
                targetPath={currentPath}
                brandColor={copy.spinnerColor}
                className="flex w-full items-center justify-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-rook-active hover:text-rook-stone focus:bg-rook-active focus:text-rook-stone"
              >
                <Check className="h-3.5 w-3.5 opacity-0" aria-hidden />
                <span className={copy.tone}>{copy.label}</span>
              </EnvSwitchButton>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Account block for the AppShell `user` slot (pinned to the sidebar
 * bottom). Built on the design-system `<UserMenu>`: name/email from the
 * auth session, initials fallback, and `<DropdownMenuItem>` children
 * for Profile / Settings / Log out. Log out stays wired to the existing
 * `useAuth().logout`; the legacy "Account settings" row is preserved as
 * the Profile + Settings entries (both deep-link to /home/account, the
 * single account surface this portal has).
 */
export const AccountMenu = () => {
  const { session, logout } = useAuth();
  if (!session.ok) return null;

  // Initials from the email — shown when there's no avatar.
  const initials = session.email.slice(0, 2).toUpperCase();
  const role = session.groups.join(", ") || "client_developer";

  return (
    // Wrapper carries the guided-tour hook: the design-system UserMenu
    // doesn't forward arbitrary data-* to its trigger, so the tour's
    // `[data-tour="topbar-account"]` selector targets this block instead
    // (it still highlights the account control region).
    <div data-tour="topbar-account">
      <UserMenu name={session.email} email={role} initials={initials}>
        <DropdownMenuItem onSelect={() => (window.location.href = "/home/account")}>
          <UserIcon className="h-3.5 w-3.5" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => (window.location.href = "/home/account")}>
          <SettingsIcon className="h-3.5 w-3.5" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout()} className="text-rook-danger">
          <LogOut className="h-3.5 w-3.5" />
          <span>Log out</span>
        </DropdownMenuItem>
      </UserMenu>
    </div>
  );
};

/**
 * Global docs button — always reachable from any /home/* route.
 * Per-section docs links inside cards (CPF-UIR-111) cover the
 * "this specific feature's docs" case; this is the "I want to
 * read everything" case. Opens https://docs.tryrook.io/ in a
 * new tab — same `target="_blank" rel="noreferrer noopener"`
 * contract as the in-card DocsLink, so the customer's mental
 * model of "docs link = book icon + new tab" is reinforced
 * everywhere.
 *
 * Why topbar (not sidebar): topbar is the universal "what is
 * the app?" surface — env + account already live here. Stripe,
 * Linear, Resend all put help/docs in the topbar too; less
 * accidental misclicks than burying it in the sidebar.
 *
 * GitHub link intentionally NOT shipped here yet — we have N
 * public repos (SDK android, ios, react-native, web, plus
 * internals) and "go to ROOK on GitHub" is ambiguous. Defer
 * until product picks a canonical landing URL. SDK-specific
 * "view source" links can ship per-context in the SDK Keys
 * view instead.
 */
const DocsButton = () => (
  <a
    href="https://docs.tryrook.io/"
    target="_blank"
    rel="noreferrer noopener"
    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 text-xs font-medium text-rook-olive transition hover:bg-white/10 hover:text-rook-stone"
    aria-label="Open ROOK docs in a new tab"
    title="Open ROOK docs in a new tab"
  >
    <BookOpen className="h-3.5 w-3.5" aria-hidden />
    <span className="hidden sm:inline">Docs</span>
  </a>
);

/**
 * Right side of the SLIM topbar (AppShell `topbarRight` slot): the
 * global selectors/indicators — Docs link + Env switcher. The account
 * menu moved OUT of the topbar to the sidebar bottom (`AccountMenu`,
 * AppShell `user` slot), per the v0.20 AppShell standard. No giant bar:
 * just the two global controls, right-aligned.
 */
export const TopbarRight = () => (
  <div className="flex items-center gap-3">
    <DocsButton />
    <EnvSwitcher />
  </div>
);
